import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { AppContext } from "../../../../core/context/appContextProvider";
import RoutingPaths from "../../../../core/routing/routingPaths";
import useRole from "../../../../core/routing/useRole";
import {
  getReviewSummary,
  updateCapitalCallsStatus,
} from "../../../../services/capitalCalls.service";
import { useCapitalCallDetailsEffect } from "../../../../services/hooks/useCapitalCallsEffect/useCapitalCallDetailsEffect.hooks";
import { ReviewsSummary } from "../../../../utils/types/capitalCalls.type";
import { ScopeRole } from "../../../../utils/types/user.type";
import {
  REVIEW_DOCUMENT_ERROR,
  REVIEW_DOCUMENT_SUCCESS,
  UPDATE_STATUS_ERROR,
} from "../constants";

type RouteProp = {
  id: string;
};
import { ReviewersList } from "../constants";

export const UsePublish = () => {
  const [reviewSummary, setReviewSummary] = useState<ReviewsSummary>();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmPublish, setConfirmPublish] = useState<boolean>(false);
  const [toPublish, setToPublish] = useState<boolean>(false);
  const [toReview, setToReview] = useState<boolean>(false);
  const [formattedReviewSummary, setFormattedReviewSummary] = useState<any>();
  const [usernames, setUsernames] = useState<string[]>([]);
  const params = useParams<RouteProp>();
  const { informationAlert, state } = useContext(AppContext);
  const { capitalCall } = useCapitalCallDetailsEffect(params?.id);

  const history = useHistory();

  const headerTitles = useMemo(() => {
    const headers =
      reviewSummary?.reviews[0].approvals.map((approval) => approval.role) ||
      [];
    const filteredHeaders = headers.filter((header, index) => {
      return headers.indexOf(header) === index;
    });

    return filteredHeaders;
  }, [reviewSummary]);

  const dummyTitle: number[] = [1, 2, 3, 4, 5];
  const { hasRole: isSuperAdminOrClientAdmin } = useRole([
    ScopeRole.SUPER_ADMIN,
    ScopeRole.ARK_CLIENT_ADMIN,
    ScopeRole.BASIC_ADMIN,
  ]);

  useEffect(() => {
    getSummaryOfReviews();
  }, [params?.id]);

  useEffect(() => {
    setStatus(capitalCall?.status);
  }, [capitalCall]);
  const selectReviewer = (member: string) => {
    switch (member) {
      case ReviewersList.Associate:
        return "Associate";
      case ReviewersList.Director:
        return "Director";
      case ReviewersList.FundController:
        return "Fund Controller";
      case ReviewersList.FundUserReadOnly:
        return "Fund User Read Only";
      case ReviewersList.Manager:
        return "Manager";
      default:
        return "";
    }
  };
  const allowEdit = useMemo(() => {
    if (params?.id !== "new") {
      if (
        state.loginUser.currentUser?.username.toLowerCase() ===
          capitalCall?.createdBy?.toLowerCase() &&
        capitalCall?.status?.toLowerCase() !== "published"
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }, [params.id, capitalCall, state]);
  const allowPublish = useMemo(() => {
    return usernames.includes(
      state.loginUser.currentUser?.username.toLowerCase() || ""
    );
  }, [usernames]);
  const getSummaryOfReviews = async () => {
    try {
      setLoading(true);
      await getReviewSummary(params?.id || "").then((data) => {
        if (data) setReviewSummary(data);

        let reviewerApprovals = false;
        const createdBy = data?.createdBy;
        let isFlaggedReview = false;
        let tosubmitReview = false;
        const formattedReviews = [];
        const userNameList: string[] = [];

        for (const review of data.reviews) {
          const approvals: any = { investor: review.investor };

          for (const approval of review.approvals) {
            !userNameList.includes(approval?.username?.toLowerCase()) &&
              userNameList.push(approval?.username?.toLowerCase());
            approvals[approval.role] = approvals[approval.role]
              ? [
                  ...approvals[approval.role],
                  {
                    id: approval.id,
                    username: approval.username,
                    tenantId: approval.tenantId,
                    approved: approval.approved,
                  },
                ]
              : [
                  {
                    id: approval.id,
                    username: approval.username,
                    tenantId: approval.tenantId,
                    approved: approval.approved,
                  },
                ];
          }

          formattedReviews.push(approvals);
        }
        setFormattedReviewSummary(formattedReviews);
        setUsernames(userNameList);
        data?.reviews?.forEach((review) => {
          const countApprovals = review.approvals.length;
          let count = 0;

          review.approvals.forEach((approval) => {
            if (approval.approved === false) {
              isFlaggedReview = true;

              return;
            }

            if (approval.approved === true) {
              count = count + 1;
            }
            if (approval.approved === true && approval.username === createdBy) {
              tosubmitReview = true;
            }
          });
          if (count === countApprovals) {
            reviewerApprovals = true;
          }
        });

        const publish = !isFlaggedReview && reviewerApprovals;
        const allowsubmitreview = !isFlaggedReview && tosubmitReview;

        setToPublish(publish);
        setToReview(allowsubmitreview);
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const submitReview = async () => {
    try {
      setLoading(true);
      await updateCapitalCallsStatus(params?.id, "REVIEWING");
      setLoading(false);
      informationAlert(REVIEW_DOCUMENT_SUCCESS, "success");
      setStatus("REVIEWING");
      setTimeout(() => {
        history.push(RoutingPaths.CapitalCalls);
      }, 1000);
    } catch (error) {
      informationAlert(REVIEW_DOCUMENT_ERROR, "error");
      setLoading(false);
    }
  };
  const handlePublishCancel = () => {
    setConfirmPublish(false);
  };

  const handlePublishConfirm = async () => {
    try {
      setStatus("PUBLISHED");
      setConfirmPublish(false);
      await updateCapitalCallsStatus(params?.id, "PUBLISHED");
      setTimeout(() => {
        history.push(RoutingPaths.CapitalCalls);
      }, 1000);
    } catch (error) {
      setStatus(capitalCall?.status);
      informationAlert(UPDATE_STATUS_ERROR, "error");
    }
  };

  return {
    reviewSummary,
    status,
    selectReviewer,
    submitReview,
    confirmPublish,
    loading,
    handlePublishCancel,
    handlePublishConfirm,
    toPublish,
    toReview,
    setConfirmPublish,
    isSuperAdminOrClientAdmin,
    reviewersLength: reviewSummary?.reviews[0]?.approvals?.length || 0,
    allowEdit,
    headerTitles,
    dummyTitle,
    formattedReviewSummary,
    allowPublish,
  };
};
